/**
 * AuthContext — exposes decoded Cognito ID-token claims to the React tree.
 *
 * What it provides:
 *   - `roles`: the non-`clinic-` strings from `cognito:groups`
 *     (e.g. `["RCM_ACCOUNT_MANAGER"]`)
 *   - `clinicIds`: the `clinic-<id>` strings from `cognito:groups`,
 *     stripped to the bare clinic_id and parsed to numbers where possible
 *     (e.g. `[87326, 91234]`)
 *   - `tenantId`, `displayName`, `email`, `sub` (from claims)
 *   - `bypass`: true when VITE_AUTH_BYPASS is on (gives the rest of the
 *     app a stable "we're in dev" signal so it can suppress the
 *     Authenticator while still pretending an AM is logged in)
 *
 * CRITICAL: per the v0.5.0 backend handoff and the Tensaw Auth Guide §7,
 * these values are UI-advisory only. They can be used to hide a button,
 * pre-fill the clinic picker, or short-circuit a 403 with an immediate
 * "you can't go here" page. The BACKEND is the source of truth and
 * re-verifies on every request. Never use these for security gates.
 *
 * Refresh handling: the claims are refreshed once per minute via
 * fetchAuthSession(), and immediately on the post-login mount. Refresh
 * doesn't tear down the component tree.
 */
import { createContext, useContext, useEffect, useRef, useState } from "react";
import type { ReactNode } from "react";
import { fetchAuthSession } from "aws-amplify/auth";

export interface AuthClaims {
  /** Cognito sub (stable user UUID). null in bypass mode. */
  sub: string | null;
  /** Cognito email claim. null in bypass mode. */
  email: string | null;
  /** Display name from the Pre-Token Lambda. null when unavailable. */
  displayName: string | null;
  /** tenant_id injected by the Pre-Token Lambda from custom:tenant_id. */
  tenantId: string | null;
  /** Non-`clinic-` strings from cognito:groups, exact-match Role enum. */
  roles: string[];
  /**
   * Numeric clinic IDs derived from `clinic-<id>` groups. Non-numeric
   * suffixes are dropped — clinic_id is a number in the dashboard schema,
   * but the Cognito group convention allows string slugs ("clinic-foo"),
   * so we filter to ones that parse cleanly.
   */
  clinicIds: number[];
  /** True when running in VITE_AUTH_BYPASS=true (dev) mode. */
  bypass: boolean;
}

const BYPASS_CLAIMS: AuthClaims = {
  sub: "dev-bypass-user",
  email: "dev@example.com",
  displayName: "Dev Bypass User",
  tenantId: "iris",
  // In bypass mode we pretend the dev is an AM with access to everything.
  // The backend's BypassVerifier returns RCM_OPS_REVIEWER, but for UI
  // gating purposes RCM_ACCOUNT_MANAGER lets the dashboard render.
  roles: ["RCM_ACCOUNT_MANAGER"],
  clinicIds: [],
  bypass: true,
};

const EMPTY_CLAIMS: AuthClaims = {
  sub: null,
  email: null,
  displayName: null,
  tenantId: null,
  roles: [],
  clinicIds: [],
  bypass: false,
};

const AuthContext = createContext<AuthClaims>(EMPTY_CLAIMS);

/**
 * Decode the claims object Amplify hands us. Cognito's groups claim is
 * `cognito:groups`; we split it into roles (non-`clinic-`) and clinic_ids
 * (`clinic-<id>`). A clinic group whose suffix doesn't parse to a number
 * is dropped silently rather than throwing — the dashboard's clinic_id
 * is numeric, so a non-numeric slug is fine on the wire but irrelevant
 * to this app.
 */
export function decodeClaimsPayload(payload: Record<string, unknown> | undefined): AuthClaims {
  if (!payload) return EMPTY_CLAIMS;
  const groups = Array.isArray(payload["cognito:groups"])
    ? (payload["cognito:groups"] as string[])
    : [];

  const roles: string[] = [];
  const clinicIds: number[] = [];
  for (const g of groups) {
    if (typeof g !== "string") continue;
    if (g.startsWith("clinic-")) {
      const suffix = g.slice("clinic-".length);
      const n = Number(suffix);
      if (Number.isFinite(n) && Number.isInteger(n)) {
        clinicIds.push(n);
      }
    } else {
      roles.push(g);
    }
  }

  return {
    sub: typeof payload.sub === "string" ? payload.sub : null,
    email: typeof payload.email === "string" ? payload.email : null,
    displayName: typeof payload.display_name === "string" ? payload.display_name : null,
    tenantId: typeof payload.tenant_id === "string" ? payload.tenant_id : null,
    roles,
    clinicIds,
    bypass: false,
  };
}

export interface AuthProviderProps {
  bypass: boolean;
  children: ReactNode;
}

/**
 * Loads claims from the current Amplify session and refreshes every
 * minute. In bypass mode, supplies a static set of claims and never
 * touches Amplify (so dev doesn't need a Cognito pool to even start).
 */
export function AuthProvider({ bypass, children }: AuthProviderProps) {
  const [claims, setClaimsRaw] = useState<AuthClaims>(bypass ? BYPASS_CLAIMS : EMPTY_CLAIMS);
  const lastKey = useRef<string>(JSON.stringify(claims));

  // Setter that only updates state when the serialized claims actually
  // changed — prevents the once-per-minute refresh from invalidating the
  // React tree when nothing's different.
  const setClaims = (next: AuthClaims) => {
    const key = JSON.stringify(next);
    if (key === lastKey.current) return;
    lastKey.current = key;
    setClaimsRaw(next);
  };

  useEffect(() => {
    if (bypass) return;

    let cancelled = false;
    const load = async () => {
      try {
        const session = await fetchAuthSession();
        const payload = session.tokens?.idToken?.payload as
          | Record<string, unknown>
          | undefined;
        if (!cancelled) setClaims(decodeClaimsPayload(payload));
      } catch {
        if (!cancelled) setClaims(EMPTY_CLAIMS);
      }
    };

    void load();
    const id = window.setInterval(() => void load(), 60_000);
    return () => {
      cancelled = true;
      window.clearInterval(id);
    };
  }, [bypass]);

  return <AuthContext.Provider value={claims}>{children}</AuthContext.Provider>;
}

/** Read the decoded claims. Returns EMPTY_CLAIMS outside an AuthProvider. */
export function useAuth(): AuthClaims {
  return useContext(AuthContext);
}

/**
 * Convenience: true iff the user has `RCM_ACCOUNT_MANAGER` in their
 * roles (or we're in bypass mode). Used by RoleGuard.
 */
export function useIsAccountManager(): boolean {
  const { roles, bypass } = useAuth();
  if (bypass) return true;
  return roles.includes("RCM_ACCOUNT_MANAGER");
}
